"use client";

// ---------------------------------------------------------------------------
// Body Surface Area Calculator — Data & Utilities
//
// Defines step identifiers, unit constants, field definitions, and the
// Mosteller BSA formula used by the HCP Body Surface Area Calculator component.
//
// Component:  src/components-hcp/body-surface-area-calculator/
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Step ID constants
// ---------------------------------------------------------------------------

export const BSA_CALC_STEP = {
  INPUTS: 'inputs',
};

// ---------------------------------------------------------------------------
// Unit constants
// ---------------------------------------------------------------------------

export const BSA_UNIT_HEIGHT = {
  IMPERIAL: 'imperial', // feet + inches
  METRIC:   'metric',   // centimeters
};

export const BSA_UNIT_WEIGHT = {
  IMPERIAL: 'imperial', // pounds
  METRIC:   'metric',   // kilograms
};

// ---------------------------------------------------------------------------
// Step definitions
// (Mirrored structure from gc-calculator-data for consistency)
// ---------------------------------------------------------------------------

export const BSA_STEPS = [
  {
    id:    BSA_CALC_STEP.INPUTS,
    label: 'Patient Measurements',
  },
];

// ---------------------------------------------------------------------------
// Unit conversion helpers
// ---------------------------------------------------------------------------

/**
 * Convert height from feet + inches to centimeters.
 * @param {string|number} feet
 * @param {string|number} inches
 * @returns {number} height in cm
 */
export function toHeightCm(feet, inches) {
  const ft = parseFloat(feet) || 0;
  const inc = parseFloat(inches) || 0;
  return (ft * 30.48) + (inc * 2.54);
}

/**
 * Convert height from centimeters to feet + inches.
 * @param {number} cm
 * @returns {{ feet: number, inches: number }}
 */
export function fromHeightCm(cm) {
  const totalInches = cm / 2.54;
  return {
    feet:   Math.floor(totalInches / 12),
    inches: Math.round(totalInches % 12),
  };
}

/**
 * Convert weight from pounds to kilograms.
 * @param {string|number} lbs
 * @returns {number} weight in kg
 */
export function toWeightKg(lbs) {
  return (parseFloat(lbs) || 0) / 2.2046;
}

// ---------------------------------------------------------------------------
// BSA formula — DuBois (1916)
// BSA (m²) = (W^0.425 × H^0.725) × 0.007184
// ---------------------------------------------------------------------------

/**
 * Compute Body Surface Area using the DuBois formula.
 * DuBois formula: BSA = (W^0.425 × H^0.725) × 0.007184
 * Used in CRENESSITY trials.
 * @param {{ heightCm: number, weightKg: number }} params
 * @returns {number|null} BSA in m², or null if inputs are invalid
 */
export function computeBSA({ heightCm, weightKg }) {
  if (!heightCm || !weightKg || heightCm <= 0 || weightKg <= 0) return null;
  return Math.pow(weightKg, 0.425) * Math.pow(heightCm, 0.725) * 0.007184;
}
